// ── Alert-Wizard ──────────────────────────────────────────────────────────────
// Design-Sprache: gleiche Linie wie ExportSheet (hairlines, große Display-Headline,
// micro-caps in T.red als Akzent, KEINE rot-getönten Container, keine Pills mit
// Farbfüllung, keine Box-in-Box-Stapel). Schritt-Indikator als zarte Punkte oben.
//
// Schritte:
//   0) Permission (nur falls noch nicht erteilt)
//   1) Coin wählen (Portfolio-Liste mit Hairlines + Custom-Input)
//   2) Alert-Typ (vertikale Liste mit Icons, Sub-Caption, Hover-Arrow)
//   3) Schwellwert (Editorial-Number-Editor + relative Chips)
//   4) Übersicht (Sentence-as-Display + Wiederholung-Toggle)

import { useEffect, useMemo, useState } from 'react';
import { F, DUR, EASE } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useUserPlan } from '../context/UserPlanContext';
import { useT } from '../i18n/useT';
import { useCurrency } from '../context/CurrencyContext';
import { createAlert, ApiError } from '../services/alertsApi';
import {
  getPermissionState, enablePush, isPushSupported,
  isStandalonePWA, isIOSSafari,
} from '../services/pushClient';
import CoinImage from './CoinImage';
import { Icon, Icons } from './red/Icons';
import TierBadge from './red/TierBadge';

// ── Alert-Typen ──────────────────────────────────────────────────────────────
// label/sub sind i18n-Keys, die zur Render-Zeit über t() aufgelöst werden.
const ALERT_TYPES = [
  { id: 'price_above', labelKey: 'price_above',          tier: 'free',    subKey: 'aw_type_price_above_sub' },
  { id: 'price_below', labelKey: 'price_below',          tier: 'free',    subKey: 'aw_type_price_below_sub' },
  { id: 'pct_change',  labelKey: 'aw_type_pct_change',   tier: 'free',    subKey: 'aw_type_pct_change_sub' },
  { id: 'holding_period',  labelKey: 'holding_period',           tier: 'premium', subKey: 'aw_type_holding_period_sub' },
  { id: 'position_value',  labelKey: 'aw_type_position_value',   tier: 'premium', subKey: 'aw_type_position_value_sub' },
  { id: 'portfolio_value', labelKey: 'aw_type_portfolio_value',  tier: 'premium', subKey: 'aw_type_portfolio_value_sub' },
];

// eslint-disable-next-line no-unused-vars
const TYPE_BY_ID = Object.fromEntries(ALERT_TYPES.map(t => [t.id, t]));
const SHEET_Z = 320;

// Fiat-Währungen sind keine sinnvollen Alert-Ziele.
const FIAT_SET = new Set([
  'EUR', 'USD', 'GBP', 'CHF', 'JPY', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK',
  'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'TRY', 'CNY', 'HKD', 'SGD', 'MXN', 'BRL', 'ZAR',
  'eur', 'usd', 'gbp', 'chf', 'jpy', 'cad', 'aud', 'nzd', 'sek', 'nok', 'dkk',
  'pln', 'czk', 'huf', 'ron', 'bgn', 'try', 'cny', 'hkd', 'sgd', 'mxn', 'brl', 'zar',
]);

// ── Hauptkomponente ──────────────────────────────────────────────────────────
export default function AlertWizard({
  open, onClose, onCreated, onOpenPricing,
  txs = [], marketData = {}, prefill = null,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const { plan } = useUserPlan();
  const isPremium = plan === 'premium' || plan === 'vip';

  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) { setMounted(true); return; }
    if (mounted) {
      const id = setTimeout(() => setMounted(false), DUR.slow);
      return () => clearTimeout(id);
    }
  }, [open, mounted]);

  const [step, setStep] = useState(0);
  const [pushReady, setPushReady] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  const [coinId, setCoinId] = useState('');
  const [coinSym, setCoinSym] = useState('');
  const [type, setType] = useState('price_above');
  const [threshold, setThreshold] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [recurring, setRecurring] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Reset beim Öffnen + Prefill-Logik
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPushError(''); setSubmitError(''); setThreshold('');
    // prefill.type erlaubt es, den Wizard mit vorgewähltem Alert-Typ zu öffnen
    // (z.B. 'holding_period' aus der Freigabe-Agenda). Die Step-Logik bleibt
    // unverändert — der Typ-Schritt zeigt den Typ aktiv (und gated Premium normal).
    setType(prefill?.type || 'price_above'); setRecurring(true);

    const pid  = prefill?.coinId || prefill?.id || prefill?.coingeckoId || '';
    const psym = prefill?.sym || prefill?.symbol || (pid ? String(pid).toUpperCase() : '');
    setCoinId(pid); setCoinSym(psym);

    (async () => {
      const supported = await isPushSupported();
      const granted = supported && getPermissionState() === 'granted';
      setPushReady(granted);
      if (pid) setStep(granted ? 2 : 0);
      else if (granted) setStep(1);
    })();
  }, [open, prefill]);

  // Quick-Picks aus User-Portfolio (eindeutig pro coinId).
  // Fiat-Währungen rausfiltern — Alerts machen nur für Krypto Sinn.
  const portfolioCoins = useMemo(() => {
    const map = new Map();
    txs.forEach(t => {
      const candidates = t.typ === 'swap'
        ? [{ sym: t.coinIn, id: t.coinIdIn }, { sym: t.coinOut, id: t.coinIdOut }]
        : [{ sym: t.coin, id: t.coinId }];
      candidates.forEach(({ sym, id }) => {
        if (!id || !sym) return;
        if (FIAT_SET.has(String(sym).toUpperCase())) return;
        if (FIAT_SET.has(String(id).toLowerCase())) return;
        if (!map.has(id)) map.set(id, sym.toUpperCase());
      });
    });
    return [...map.entries()].map(([id, sym]) => ({ id, sym }));
  }, [txs]);

  const currentPrice = useMemo(() => {
    if (!coinId) return null;
    const d = marketData[coinId];
    if (!d) return null;
    const key = currency === 'EUR' ? 'price_eur' : 'price_usd';
    return typeof d[key] === 'number' ? d[key] : (typeof d.price === 'number' ? d.price : null);
  }, [coinId, currency, marketData]);

  const handleEnablePush = async () => {
    setPushBusy(true); setPushError('');
    try {
      await enablePush();
      setPushReady(true);
      setStep(coinId ? 2 : 1);
    } catch (err) {
      setPushError(err.message || t('aw_err_enable_failed'));
    } finally { setPushBusy(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true); setSubmitError('');
    try {
      const payload = {
        type,
        threshold: Number(threshold),
        currency, recurring, cooldownH: 24,
      };
      // portfolio_value ist NICHT coin-spezifisch — kein coinId mitsenden.
      if (type !== 'portfolio_value') payload.coinId = coinId;
      const result = await createAlert(payload);
      onCreated?.(result.alert);
      onClose?.();
    } catch (err) {
      if (err instanceof ApiError && err.requiresUpgrade) { onOpenPricing?.(); return; }
      setSubmitError(err.message || t('aw_err_create_failed'));
    } finally { setSubmitting(false); }
  };

  if (!mounted) return null;

  // Step-Choreographie
  const totalSteps = pushReady ? 4 : 5;        // Permission optional
  const stepIndex  = pushReady ? step - 1 : step;
  const minStep    = prefill ? 2 : (pushReady ? 1 : 0);
  const canGoBack  = step > minStep;
  const goBack     = () => setStep(s => Math.max(minStep, s - 1));

  const titleByStep = {
    0: [t('aw_title_push_l1'), t('aw_title_push_l2')],
    1: [t('aw_title_coin_l1'), t('aw_title_coin_l2')],
    2: [t('aw_title_type_l1'), t('aw_title_type_l2')],
    3: [t('aw_title_threshold_l1'), t('aw_title_threshold_l2')],
    4: [t('aw_title_review')],
  };
  const subByStep = {
    0: t('aw_sub_push'),
    1: t('aw_sub_coin'),
    2: t('aw_sub_type'),
    3: coinSym ? t('aw_sub_threshold_coin', { coin: coinSym }) : t('aw_sub_threshold'),
    4: t('aw_sub_review'),
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: SHEET_Z,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: `redFadeIn ${DUR.fast}ms ease`,
      }}
    >
      <style>{`
        @keyframes redFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes redSlideUp {
          from { transform: translateY(100%); opacity: 0.6 }
          to   { transform: translateY(0);    opacity: 1 }
        }
        @keyframes redStepIn {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        data-kb-overlay
        style={{
          position: 'relative', width: '100%', maxWidth: 480,
          background: T.surface,
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          borderRadius: '28px 28px 0 0',
          padding: '14px 24px 30px',
          paddingBottom: 'calc(30px + var(--kbpad))',
          transition: 'padding-bottom var(--dur-kb) var(--ease-kb)',
          border: `1px solid ${T.line}`,
          borderBottom: 'none',
          maxHeight: '92vh', overflowY: 'auto',
          animation: `redSlideUp ${DUR.slow}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {/* Drag handle */}
        <div style={{ width: 36, height: 4, background: T.dim, borderRadius: 2, margin: '0 auto 18px' }} />

        {/* Top controls: Back (left) + Close (right) */}
        {canGoBack && (
          <CircleBtn T={T} onClick={goBack} side="left" ariaLabel={t('back')}>
            <Icon d={Icons.back} size={14} stroke="currentColor" sw={2.4} />
          </CircleBtn>
        )}
        <CircleBtn T={T} onClick={onClose} side="right" ariaLabel={t('close')}>
          <Icon d={Icons.close} size={14} stroke="currentColor" sw={2.4} />
        </CircleBtn>

        {/* Step-Dots */}
        <StepDots T={T} total={totalSteps} index={stepIndex} />

        {/* Headline + Subtitle */}
        <Headline T={T} lines={titleByStep[step]} sub={subByStep[step]} />

        {/* Step-Container — animiert bei jedem Wechsel */}
        <div key={step} style={{ animation: `redStepIn ${DUR.slow}ms cubic-bezier(0.22, 1, 0.36, 1)` }}>
          {step === 0 && (
            <Step0Permission
              T={T} onEnable={handleEnablePush} onSkip={() => setStep(1)}
              busy={pushBusy} error={pushError}
              iosHint={isIOSSafari() && !isStandalonePWA()}
            />
          )}

          {step === 1 && (
            <Step1Coin
              T={T} portfolioCoins={portfolioCoins} marketData={marketData}
              onSelect={(id, sym) => { setCoinId(id); setCoinSym(sym); setStep(2); }}
            />
          )}

          {step === 2 && (
            <Step2Type
              T={T} isPremium={isPremium} value={type}
              onSelect={(id) => { setType(id); setStep(3); }}
              onLocked={onOpenPricing}
            />
          )}

          {step === 3 && (
            <Step3Threshold
              T={T} coinId={coinId} coinSym={coinSym} marketData={marketData}
              type={type} currentPrice={currentPrice}
              currency={currency} onCurrencyChange={setCurrency}
              value={threshold} onChange={setThreshold}
              onNext={() => setStep(4)}
            />
          )}

          {step === 4 && (
            <Step4Review
              T={T} coinSym={coinSym} marketData={marketData} coinId={coinId}
              type={type} threshold={threshold} currency={currency}
              recurring={recurring} setRecurring={setRecurring}
              submitting={submitting} error={submitError}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Atomare Bausteine ────────────────────────────────────────────────────────

function CircleBtn({ T, onClick, side = 'right', ariaLabel, children }) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      onMouseEnter={e => { e.currentTarget.style.background = T.itemHover; e.currentTarget.style.color = T.text; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sub; e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      style={{
        position: 'absolute', top: 18, [side]: 18,
        width: 32, height: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'transparent', color: T.sub,
        border: `1px solid ${T.line}`, borderRadius: '50%',
        cursor: 'pointer', padding: 0,
        transition: `background ${DUR.base}ms ease, color ${DUR.base}ms ease, transform ${DUR.base}ms cubic-bezier(.34,1.5,.5,1)`,
        WebkitTapHighlightColor: 'transparent', zIndex: 2,
      }}
    >
      {children}
    </button>
  );
}

function StepDots({ T, total, index }) {
  return (
    <div style={{
      display: 'flex', gap: 6, justifyContent: 'center',
      margin: '0 0 22px',
    }}>
      {Array.from({ length: total }).map((_, i) => {
        const active = i === index;
        const done   = i < index;
        return (
          <span key={i} style={{
            width: active ? 18 : 5, height: 5,
            borderRadius: 999,
            background: active ? T.red : done ? T.sub : T.line,
            transition: `width ${DUR.base}ms cubic-bezier(0.22, 1, 0.36, 1), background ${DUR.fast}ms ease`,
          }} />
        );
      })}
    </div>
  );
}

function Headline({ T, lines, sub }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{
        fontFamily: F.display, fontSize: 30, fontWeight: 800,
        letterSpacing: '-0.04em', color: T.text, lineHeight: 1.02,
      }}>
        {lines.map((l, i) => <div key={i}>{l}</div>)}
      </div>
      {sub && (
        <div style={{
          fontSize: 13, color: T.sub, lineHeight: 1.5, fontWeight: 500,
          marginTop: 10, maxWidth: 360,
        }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function CapsLabel({ T, children, style }) {
  return (
    <div style={{
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.18em', color: T.mute,
      marginBottom: 10, ...style,
    }}>{children}</div>
  );
}

function PrimaryCTA({ T, onClick, disabled, busy, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      style={{
        width: '100%', padding: '15px',
        background: disabled ? 'transparent' : T.red,
        color: disabled ? T.mute : '#fff',
        border: disabled ? `1px solid ${T.line}` : 'none',
        borderRadius: 3,
        fontFamily: F.family, fontSize: 14, fontWeight: 700,
        letterSpacing: '-0.01em',
        cursor: disabled || busy ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : T.redGlowCTA,
        transition: `transform ${DUR.press}ms ease, box-shadow ${DUR.base}ms ease, background ${DUR.fast}ms ease`,
        WebkitTapHighlightColor: 'transparent',
      }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = 'scale(0.985)'; }}
      onMouseUp={e   => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={e=> { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {busy ? '…' : children}
    </button>
  );
}

function GhostCTA({ T, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', padding: '13px',
        background: 'transparent', color: T.sub,
        border: 'none',
        fontFamily: F.family, fontSize: 12.5, fontWeight: 600,
        letterSpacing: '-0.005em',
        cursor: 'pointer', marginTop: 6,
        WebkitTapHighlightColor: 'transparent',
      }}
      onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
      onMouseLeave={e => { e.currentTarget.style.color = T.sub; }}
    >
      {children}
    </button>
  );
}

// ── Step 0 — Permission ─────────────────────────────────────────────────────

function Step0Permission({ T, onEnable, onSkip, busy, error, iosHint }) {
  const { t } = useT();
  return (
    <div>
      {iosHint && (
        <div style={{
          padding: '14px 0', marginBottom: 14,
          borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
          fontSize: 12.5, color: T.sub, lineHeight: 1.55,
        }}>
          <div style={{ fontFamily: F.display, fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            {t('aw_ios_title')}
          </div>
          {t('aw_ios_hint_pre')}<span style={{ color: T.text, fontWeight: 600 }}>{t('aw_ios_hint_home')}</span>{t('aw_ios_hint_post')}
        </div>
      )}

      {error && (
        <div style={{
          fontSize: 12.5, color: T.red, lineHeight: 1.5,
          padding: '12px 0', borderTop: `1px solid ${T.line}`,
          marginBottom: 14,
        }}>
          {error}
        </div>
      )}

      <PrimaryCTA T={T} onClick={onEnable} busy={busy}>{t('aw_enable_push')}</PrimaryCTA>
      <GhostCTA T={T} onClick={onSkip}>{t('aw_enable_later')}</GhostCTA>

      <div style={{
        fontSize: 11, color: T.mute, lineHeight: 1.5, marginTop: 14, textAlign: 'center',
      }}>
        {t('aw_privacy_pre')}{' '}
        <a href="https://redcrypto.de/datenschutz" target="_blank" rel="noopener noreferrer"
           style={{ color: T.sub, textDecoration: 'underline', textUnderlineOffset: 2 }}>
          {t('aw_privacy_link')}
        </a>{t('aw_privacy_post')}
      </div>
    </div>
  );
}

// ── Step 1 — Coin ───────────────────────────────────────────────────────────

function Step1Coin({ T, portfolioCoins, marketData, onSelect }) {
  const { t } = useT();
  const [custom, setCustom] = useState('');

  return (
    <div>
      {portfolioCoins.length > 0 && (
        <>
          <CapsLabel T={T}>{t('aw_from_portfolio')}</CapsLabel>
          <div style={{ marginBottom: 18 }}>
            {portfolioCoins.map((c, i) => (
              <CoinRow
                key={c.id} T={T} sym={c.sym} marketData={marketData}
                divider={i > 0}
                onClick={() => onSelect(c.id, c.sym)}
              />
            ))}
          </div>
        </>
      )}

      <CapsLabel T={T}>{t('aw_other_coin')}</CapsLabel>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '4px 0',
        borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
      }}>
        <input
          value={custom}
          onChange={e => setCustom(e.target.value.trim().toLowerCase())}
          placeholder={t('aw_coin_input_ph')}
          style={{
            flex: 1, padding: '14px 0',
            background: 'transparent', border: 'none', outline: 'none',
            color: T.text, fontFamily: F.family, fontSize: 14, fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
        />
        <button
          disabled={!custom}
          onClick={() => onSelect(custom, custom.toUpperCase())}
          style={{
            background: 'transparent', border: 'none', color: custom ? T.red : T.mute,
            fontFamily: F.family, fontSize: 12, fontWeight: 700,
            letterSpacing: '0.04em',
            cursor: custom ? 'pointer' : 'not-allowed',
            padding: '8px 0',
            WebkitTapHighlightColor: 'transparent',
          }}>
          {t('aw_choose')} →
        </button>
      </div>
    </div>
  );
}

function CoinRow({ T, sym, marketData, divider, onClick }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => {
        const ar = e.currentTarget.querySelector('[data-arrow]');
        if (ar) { ar.style.transform = 'translateX(4px)'; ar.style.color = T.text; }
      }}
      onMouseLeave={e => {
        const ar = e.currentTarget.querySelector('[data-arrow]');
        if (ar) { ar.style.transform = 'translateX(0)'; ar.style.color = T.mute; }
      }}
      style={{
        width: '100%', padding: '14px 0',
        background: 'transparent', border: 'none',
        borderTop: divider ? `1px solid ${T.line}` : 'none',
        color: T.text, textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 14,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <CoinImage sym={sym} marketData={marketData} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: F.display, fontSize: 15, fontWeight: 700,
          letterSpacing: '-0.02em', color: T.text, lineHeight: 1.2,
        }}>{sym}</div>
      </div>
      <span data-arrow style={{
        color: T.mute, fontSize: 18, lineHeight: 1, fontWeight: 300,
        transition: `transform ${DUR.base}ms cubic-bezier(0.34, 1.56, 0.64, 1), color ${DUR.base}ms ease`,
      }}>→</span>
    </button>
  );
}

// ── Step 2 — Alert-Typ ──────────────────────────────────────────────────────

function Step2Type({ T, isPremium, value, onSelect, onLocked }) {
  return (
    <div>
      {ALERT_TYPES.map((opt, i) => {
        const locked = opt.tier === 'premium' && !isPremium;
        const active = value === opt.id;
        return (
          <TypeRow
            key={opt.id} T={T} opt={opt}
            divider={i > 0}
            locked={locked} active={active}
            onClick={() => locked ? onLocked?.() : onSelect(opt.id)}
          />
        );
      })}
    </div>
  );
}

function TypeRow({ T, opt, divider, locked, active, onClick }) {
  const { t } = useT();
  return (
    <button
      onClick={onClick}
      onMouseEnter={e => {
        const ar = e.currentTarget.querySelector('[data-arrow]');
        if (ar) { ar.style.transform = 'translateX(4px)'; ar.style.color = T.text; }
      }}
      onMouseLeave={e => {
        const ar = e.currentTarget.querySelector('[data-arrow]');
        if (ar) { ar.style.transform = 'translateX(0)'; ar.style.color = T.mute; }
      }}
      style={{
        width: '100%', padding: '18px 0',
        background: 'transparent', border: 'none',
        borderTop: divider ? `1px solid ${T.line}` : 'none',
        color: T.text, textAlign: 'left', cursor: 'pointer',
        display: 'flex', alignItems: 'flex-start', gap: 14,
        opacity: locked ? 0.55 : 1,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            fontFamily: F.display, fontSize: 16, fontWeight: 700,
            letterSpacing: '-0.02em', color: T.text, lineHeight: 1.2,
          }}>
            {t(opt.labelKey)}
          </div>
          {opt.tier === 'premium' && <TierBadge tier="premium" scale={0.8} />}
        </div>
        <div style={{
          fontSize: 12.5, color: T.sub, lineHeight: 1.45,
          marginTop: 4, fontWeight: 500,
        }}>{t(opt.subKey)}</div>
      </div>
      <span data-arrow style={{
        color: T.mute, fontSize: 18, lineHeight: 1, fontWeight: 300,
        transition: `transform ${DUR.base}ms cubic-bezier(0.34, 1.56, 0.64, 1), color ${DUR.base}ms ease`,
        marginTop: 2,
      }}>→</span>
    </button>
  );
}

// ── Step 3 — Schwellwert ────────────────────────────────────────────────────

function Step3Threshold({
  T, coinId, coinSym, marketData,
  type, currentPrice, currency, onCurrencyChange,
  value, onChange, onNext,
}) {
  const { t } = useT();
  const { locale } = useCurrency();
  const isPercent   = type === 'pct_change';
  const isHolding   = type === 'holding_period';
  const isPortfolio = type === 'portfolio_value';
  const moneySymbol = currency === 'EUR' ? '€' : '$';
  const unit = isHolding ? t('aw_unit_days')
    : isPercent ? '%'
    : moneySymbol;
  const num = Number(value);
  const valid = isHolding
    ? (value && !isNaN(num) && num >= 1 && num <= 365)
    : (value && !isNaN(num) && num > 0);

  const setRelative = (pct) => {
    if (!currentPrice || isPercent || isHolding || isPortfolio) return;
    onChange(String((currentPrice * (1 + pct / 100)).toFixed(2)));
  };

  const capsLabel = isHolding ? t('aw_caps_lead_time')
    : isPortfolio ? t('aw_caps_portfolio_threshold')
    : isPercent ? t('aw_caps_move_24h')
    : type === 'price_above' ? t('aw_caps_threshold_up')
    : t('aw_caps_threshold_down');

  return (
    <div>
      {/* Coin-Header — entfällt beim Portfolio-Wert (nicht coin-spezifisch) */}
      {!isPortfolio && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '0 0 18px', marginBottom: 18,
          borderBottom: `1px solid ${T.line}`,
        }}>
          <CoinImage sym={coinSym} marketData={marketData} size={32} />
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: F.display, fontSize: 14, fontWeight: 700,
              letterSpacing: '-0.01em', color: T.text, lineHeight: 1.2,
            }}>{coinSym}</div>
            {!isPercent && !isHolding && currentPrice != null && (
              <div style={{
                fontSize: 11, color: T.sub, marginTop: 3, lineHeight: 1.2,
              }}>
                {t('aw_current_price', { price: fmtPrice(currentPrice, locale), sym: moneySymbol })}
              </div>
            )}
          </div>
          {!isPercent && !isHolding && (
            <CurrencyToggle T={T} currency={currency} onChange={onCurrencyChange} />
          )}
        </div>
      )}

      {/* Portfolio: Währungs-Toggle oben (kein Coin-Header) */}
      {isPortfolio && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          padding: '0 0 12px', marginBottom: 14,
          borderBottom: `1px solid ${T.line}`,
        }}>
          <CurrencyToggle T={T} currency={currency} onChange={onCurrencyChange} />
        </div>
      )}

      {/* Großer Number-Editor */}
      <CapsLabel T={T}>{capsLabel}</CapsLabel>

      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10,
        padding: '4px 0 14px',
        borderBottom: `1px solid ${value ? T.red : T.line}`,
        transition: `border-color ${DUR.base}ms ease`,
      }}>
        <input
          value={value}
          onChange={e => onChange(e.target.value.replace(',', '.'))}
          inputMode="decimal"
          placeholder={isHolding ? '7' : isPercent ? '5' : '70 000'}
          style={{
            flex: 1, minWidth: 0,
            padding: 0, background: 'transparent', border: 'none', outline: 'none',
            color: T.text,
            fontFamily: F.display, fontSize: 42, fontWeight: 800,
            letterSpacing: '-0.04em', lineHeight: 1,
            fontFeatureSettings: '"tnum" 1',
          }}
        />
        <span style={{
          fontFamily: F.display, fontSize: 22, fontWeight: 700,
          color: T.sub, letterSpacing: '-0.02em',
        }}>{unit}</span>
      </div>

      {/* Haltefrist: Vorwarnzeit-Chips */}
      {isHolding && (
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: '14px 0 22px',
        }}>
          {[1, 7, 14, 30].map(d => (
            <button key={d} onClick={() => onChange(String(d))} style={{
              background: 'transparent', border: 'none',
              padding: '6px 4px', cursor: 'pointer',
              color: Number(value) === d ? T.red : T.sub,
              fontFamily: F.mono, fontSize: 12.5,
              fontWeight: 600, letterSpacing: '-0.01em',
              transition: `color ${DUR.fast}ms ease`,
              WebkitTapHighlightColor: 'transparent',
            }}
              onMouseEnter={e => e.currentTarget.style.color = T.text}
              onMouseLeave={e => e.currentTarget.style.color = Number(value) === d ? T.red : T.sub}
            >{d === 1 ? t('aw_n_day', { n: d }) : t('aw_n_days', { n: d })}</button>
          ))}
        </div>
      )}

      {/* Relative Adjustments — nur bei Preis-Alerts */}
      {!isPercent && !isHolding && !isPortfolio && currentPrice != null && (
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          padding: '14px 0 22px',
        }}>
          {[-10, -5, +5, +10].map(p => (
            <button key={p} onClick={() => setRelative(p)} style={{
              background: 'transparent', border: 'none',
              padding: '6px 4px', cursor: 'pointer',
              color: T.sub, fontFamily: F.mono, fontSize: 12.5,
              fontWeight: 600, letterSpacing: '-0.01em',
              transition: `color ${DUR.fast}ms ease`,
              WebkitTapHighlightColor: 'transparent',
            }}
              onMouseEnter={e => e.currentTarget.style.color = T.text}
              onMouseLeave={e => e.currentTarget.style.color = T.sub}
            >{p > 0 ? `+${p} %` : `${p} %`}</button>
          ))}
        </div>
      )}

      <PrimaryCTA T={T} onClick={onNext} disabled={!valid}>{t('next')}</PrimaryCTA>
    </div>
  );
}

function CurrencyToggle({ T, currency, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {['EUR', 'USD'].map(c => {
        const active = currency === c;
        return (
          <button key={c} onClick={() => onChange(c)} style={{
            background: 'transparent', border: 'none', padding: '6px 8px',
            color: active ? T.text : T.mute,
            fontFamily: F.mono, fontSize: 11.5, fontWeight: 700,
            letterSpacing: '0.04em',
            cursor: 'pointer',
            transition: `color ${DUR.fast}ms ease`,
            WebkitTapHighlightColor: 'transparent',
          }}>{c}</button>
        );
      })}
    </div>
  );
}

// ── Step 4 — Übersicht ──────────────────────────────────────────────────────

function Step4Review({
  T, coinSym, marketData, coinId,
  type, threshold, currency, recurring, setRecurring,
  submitting, error, onSubmit,
}) {
  const { t } = useT();
  const isPercent   = type === 'pct_change';
  const isHolding   = type === 'holding_period';
  const isPortfolio = type === 'portfolio_value';
  const unit = isHolding ? (Number(threshold) === 1 ? t('aw_unit_day') : t('aw_unit_days'))
    : isPercent ? '%'
    : currency;
  const verb = { price_above: t('aw_verb_above'), price_below: t('aw_verb_below') }[type] || t('aw_verb_reaches');
  const niceThreshold = formatGroup(threshold);
  const accentSpan = {
    color: T.red, fontWeight: 800,
    fontFeatureSettings: '"tnum" 1', letterSpacing: '-0.03em',
  };

  return (
    <div>
      {/* Sentence-as-Display */}
      <div style={{
        padding: '14px 0 22px', marginBottom: 18,
        borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          {isPortfolio
            ? <div style={{
                width: 28, height: 28, borderRadius: '50%',
                border: `1px solid ${T.line}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: T.sub, fontFamily: F.display, fontSize: 14, fontWeight: 800,
              }}>∑</div>
            : <CoinImage sym={coinSym} marketData={marketData} size={28} />}
          <div style={{
            fontFamily: F.display, fontSize: 14, fontWeight: 700,
            letterSpacing: '-0.01em', color: T.text,
          }}>{isPortfolio ? t('aw_portfolio') : coinSym}</div>
        </div>
        <div style={{
          fontFamily: F.display, color: T.text,
          fontSize: 22, fontWeight: 700, letterSpacing: '-0.025em',
          lineHeight: 1.18,
        }}>
          {isHolding ? (
            <>
              {t('aw_review_holding_lead')} <span style={{ color: T.sub, fontWeight: 600 }}>{t('aw_review_holding_connector')} </span>
              <span style={accentSpan}>{niceThreshold}{' '}{unit}</span>.
            </>
          ) : isPortfolio ? (
            <>
              {t('aw_review_portfolio_lead')} <span style={{ color: T.sub, fontWeight: 600 }}>{t('aw_review_portfolio_connector')} </span>
              <span style={accentSpan}>{niceThreshold}{' '}{unit}</span>.
            </>
          ) : (
            <>
              {isPercent ? t('aw_review_move_lead_pct') : t('aw_review_move_lead')}
              <span style={{ color: T.sub, fontWeight: 600 }}>{isPercent ? ` ${t('aw_review_at_least')} ` : `${verb} `}</span>
              <span style={accentSpan}>{niceThreshold}{' '}{unit}</span>.
            </>
          )}
        </div>
      </div>

      {/* Wiederkehrend-Toggle als Hairline-Row */}
      <button
        onClick={() => setRecurring(!recurring)}
        style={{
          width: '100%', padding: '16px 0',
          background: 'transparent', border: 'none',
          borderBottom: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer', textAlign: 'left',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: F.display, fontSize: 14, fontWeight: 700,
            letterSpacing: '-0.01em', color: T.text,
          }}>{t('aw_recurring')}</div>
          <div style={{ fontSize: 12, color: T.sub, marginTop: 3, lineHeight: 1.4 }}>
            {recurring ? t('aw_recurring_on') : t('aw_recurring_off')}
          </div>
        </div>
        <Switch T={T} on={recurring} />
      </button>

      {error && (
        <div style={{
          fontSize: 12.5, color: T.red, lineHeight: 1.5,
          padding: '14px 0', borderBottom: `1px solid ${T.line}`,
        }}>{error}</div>
      )}

      <div style={{ marginTop: 22 }}>
        <PrimaryCTA T={T} onClick={onSubmit} busy={submitting}>{t('aw_create_alert')}</PrimaryCTA>
      </div>
    </div>
  );
}

function Switch({ T, on }) {
  return (
    <div style={{
      width: 38, height: 22, borderRadius: 999,
      background: on ? T.red : T.line,
      position: 'relative',
      transition: `background ${DUR.base}ms ease`,
      flexShrink: 0,
    }}>
      <div style={{
        position: 'absolute', top: 2,
        left: on ? 18 : 2, width: 18, height: 18,
        borderRadius: '50%', background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        transition: `left ${DUR.base}ms cubic-bezier(0.22, 1, 0.36, 1)`,
      }} />
    </div>
  );
}

// ── Format-Helper ────────────────────────────────────────────────────────────

function fmtPrice(n, locale = 'de-DE') {
  if (typeof n !== 'number') return '—';
  if (n >= 1) return n.toLocaleString(locale, { maximumFractionDigits: 2 });
  return n.toLocaleString(locale, { maximumFractionDigits: 6 });
}
function formatGroup(v) {
  const n = Number(v);
  if (!isFinite(n)) return v;
  if (Math.abs(n) >= 1000) return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  return String(v);
}
